import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { useQueryClient } from '@tanstack/react-query';
import type { DashboardT, WidgetT } from 'business/definitions';
import GeneralModal from 'components/SharedComponents/Modals/GeneralModal';
import { trackEvent } from 'helpers/trackerHelpers';
import { useIsExploreUser } from 'hooks/client';
import { useGetDashboardFromSlugOrMetadata } from 'hooks/useGetDashboardFromSlugOrMetadata';
import type { WidgetPositionContextType } from 'lib/layout/context/WidgetPositionContext';
import CreateDashboardButton from 'modules/decision/components/CreateDashboardForm/CreateDashboardButton';
import DashboardList from 'modules/decision/components/DashboardList/DashboardList';
import { useExploreContext } from 'modules/decision/pages/Explore/components/Contexts/ExploreContext';
import type { SettingsState } from 'modules/decision/pages/Explore/hooks/reducers/types';
import type { ChangeEvent } from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { filterDashboardsByTitle } from 'routes/Library/helpers';
import { useFetchActiveDashboards } from 'shared/api/getDashboardsList';
import AddToDashboardItem from 'shared/components/ChartEditMenu/AddToDashboardItem';
import { useAddChartToDashboard } from 'shared/components/ChartEditMenu/hooks/addChartToDashboard';
import { useDashboardWidgetContext } from 'shared/components/WidgetsLayout/context/WidgetContext';
import { useSelector } from 'store/hooks';

type AddToDashboardModalProps = {
    show: boolean;
    closeModal: () => void;
    chartKey?: number | null;
    settings?: Pick<
        SettingsState,
        'chartTitle' | 'selectedViz' | 'chartSettings'
    >;
    isInDashboard?: boolean;
    query?: ExploreQueryT;
    widgetPosition?: WidgetPositionContextType | WidgetT['position'];
};

const AddToDashboardModal = ({
    show,
    chartKey = undefined,
    closeModal,
    settings = undefined,
    isInDashboard = undefined,
    query = undefined,
    widgetPosition = undefined,
}: AddToDashboardModalProps): JSX.Element => {
    const { t } = useTranslation();
    const { explore } = useExploreContext();
    const queryClient = useQueryClient();
    const [shouldInsertChartBelow, setShouldInsertChartBelow] =
        useState<boolean>(
            () => localStorage.getItem('shouldInsertChartBelow') === 'true',
        );

    const toggleInsertChartBelow = () => {
        setShouldInsertChartBelow((prev) => {
            const newValue = !prev;
            localStorage.setItem('shouldInsertChartBelow', newValue.toString());
            return newValue;
        });
    };

    const homeDashboardSlug = useSelector(
        (state) => state.vision.user.homeDashboardSlug,
    );
    const isExploreUser = useIsExploreUser();
    const currentDashboard = useGetDashboardFromSlugOrMetadata();
    const { data: activeDashboards } = useFetchActiveDashboards();
    // Only show dashboard where we have edit right
    const dashboards = activeDashboards
        ? activeDashboards.filter((d) => d.canEdit)
        : [];

    const { key: widgetKey } = useDashboardWidgetContext();
    const { addChartToDashboard } = useAddChartToDashboard();

    const addChart = (dashboard: DashboardT) =>
        addChartToDashboard(
            explore,
            dashboard,
            chartKey,
            widgetKey,
            isInDashboard,
            query,
            settings,
            undefined,
            undefined,
            currentDashboard?.key === dashboard.key && shouldInsertChartBelow
                ? widgetPosition
                : undefined,
        );

    // TODO we need to filter out dashboards that we cannot edit using canEditDashboard

    /* Handle dashboards selection */
    const [selection, setSelection] = useState<string[]>([]);
    const submit = () => {
        selection.forEach((slug) => {
            const dashboard = dashboards.find((dash) => dash.slug === slug);
            if (dashboard !== undefined) {
                addChart(dashboard);
                trackEvent('decision/indicator/saved', { chartKey });
                queryClient.invalidateQueries({
                    queryKey: ['widget', chartKey],
                });
            }
        });
        closeModal();
    };

    /* Handle the selection inside the library. We useCallback to avoid reference issues */
    const changeSelection = useCallback(
        (slug: string) => {
            let newSelection;
            if (selection.includes(slug)) {
                newSelection = selection.filter((s) => s !== slug);
            } else {
                newSelection = [...selection, slug];
            }
            setSelection(newSelection);
        },
        [selection],
    );
    const [filterString, setFilterString] = useState('');
    const filteredDashboards = filterDashboardsByTitle(
        dashboards,
        filterString,
        [],
    );
    const onChangeSearch = (e: ChangeEvent<HTMLInputElement>) =>
        setFilterString(e.target.value);

    const onCreateDashboard = (dashboard: DashboardT) => {
        addChart(dashboard);
        trackEvent('decision/indicator/saved', { chartKey });
        closeModal();
    };

    const addToDashboardItem = (dashboard: DashboardT) => (
        <AddToDashboardItem
            key={dashboard.slug}
            dashboard={dashboard}
            isSelected={selection.includes(dashboard.slug)}
            changeSelection={changeSelection}
            isCurrentDashboard={currentDashboard?.key === dashboard.key}
            shouldInsertChartBelow={shouldInsertChartBelow}
            toggleInsertChartBelow={toggleInsertChartBelow}
        />
    );

    const ModalContent = (
        <>
            <TextField
                value={filterString}
                onChange={onChangeSearch}
                autoFocus
                variant="outlined"
                margin="dense"
                size="small"
                fullWidth
                label={t('dashboards.search')}
                slotProps={{
                    input: {
                        endAdornment: (
                            <InputAdornment position="end">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    },
                }}
            />
            {isExploreUser && (
                <CreateDashboardButton onCreate={onCreateDashboard} />
            )}
            {!!dashboards.length && (
                <DashboardList
                    dashboards={filteredDashboards}
                    homeDashboardSlug={homeDashboardSlug}
                    dashboardItemRender={(dashboard) =>
                        addToDashboardItem(dashboard)
                    }
                />
            )}
        </>
    );

    const closeButton = {
        text: t('common.close'),
        onClick: closeModal,
    };
    const containedButton = {
        text: t('common.add'),
        onClick: submit,
        className: 'add-to-dashboard-confirm',
    };

    return (
        <GeneralModal
            isOpen={show}
            onClose={closeModal}
            showCloseButton={false}
            shouldCloseOnBackdropClick
            containedButton={containedButton}
            textButton={closeButton}
        >
            {ModalContent}
        </GeneralModal>
    );
};

export default AddToDashboardModal;
